import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useHistory, useParams } from 'react-router-dom';
import { Button } from 'react-bootstrap';
import {
    ArrowLeft,
    ArrowRight,
    Banknote,
    BookOpen,
    Bot,
    CheckCircle2,
    Clock,
    Coins,
    FileText,
    FolderKanban,
    FolderOpen,
    Inbox,
    Landmark,
    LayoutDashboard,
    Link2,
    Lock,
    Plug,
    Receipt,
    ScanLine,
    ShoppingCart,
    Sparkles,
    Tags,
    Users,
    Wallet,
} from 'lucide-react';
import axios from '../../services/AxiosInstance';
import { getApiUrl } from '../../utils/ApiUtils';
import { useFeatures } from '../../context/FeatureContext';

/**
 * The interstitial a feature gate redirects to. One job: name what the user
 * tried to open, and show the shortest path to it — current plan → the
 * cheapest plan that includes the feature. Plan COMPARISON stays on
 * Inställningar → Prenumeration; this page only points there.
 *
 * Icons are lucide, matching the sidebar the user just clicked in — the
 * feature should look like the thing they were navigating to, not like an
 * error.
 */
const FEATURE_DEFINITIONS = {
    bookkeeping: { name: 'Bokföring', icon: BookOpen, description: 'Grundläggande bokföring och huvudbok.' },
    invoicing: { name: 'Fakturering', icon: Receipt, description: 'Skapa och skicka kundfakturor.' },
    invoicing_quotes: { name: 'Offerter', icon: FileText, description: 'Skapa offerter och gör om dem till fakturor.' },
    supplier_invoices: { name: 'Leverantörsfakturor', icon: ShoppingCart, description: 'Ta emot, godkänn och bokför inköpsfakturor.' },
    payroll: { name: 'Lönehantering', icon: Banknote, description: 'Lönekörningar, lönespecar och arbetsgivardeklaration.' },
    personell_management: { name: 'Personalhantering', icon: Users, description: 'Hantera anställda och anställningsavtal.' },
    banking_integration: { name: 'Bankhändelser', icon: Landmark, description: 'Importera och stäm av banktransaktioner.' },
    bank_feed: { name: 'Bankkoppling', icon: Link2, description: 'Hämta banktransaktioner automatiskt från din bank.' },
    multi_currency: { name: 'Flera valutor', icon: Coins, description: 'Bokföring i andra valutor än SEK.' },
    api_access: { name: 'API-åtkomst', icon: Plug, description: 'Programmatisk åtkomst via REST-API.' },
    document_management: { name: 'Dokumentarkiv', icon: FolderOpen, description: 'Samla och organisera företagets dokument.' },
    time_tracking: { name: 'Tidrapportering', icon: Clock, description: 'Rapportera tid och koppla den till projekt.' },
    expense_management: { name: 'Utlägg', icon: Wallet, description: 'Registrera utlägg och få dem attesterade och bokförda.' },
    project_management: { name: 'Projekt', icon: FolderKanban, description: 'Följ upp intäkter och kostnader per projekt.' },
    dashboard_widget: { name: 'Översikt', icon: LayoutDashboard, description: 'Nyckeltal och rapporter för företaget.' },
    email_inbox_scanning: { name: 'E-postinkorg', icon: Inbox, description: 'Hämta fakturor automatiskt från anslutna e-postkonton.' },
    ai_assistant: { name: 'AI-assistent', icon: Sparkles, description: 'Chatta med Finn och agentteamet om din bokföring.' },
    ai_ocr: { name: 'Fakturatolkning', icon: ScanLine, description: 'Läs av fakturor automatiskt med AI.' },
    ai_categorization: { name: 'AI-kategorisering', icon: Tags, description: 'Föreslå konton automatiskt vid bokföring.' },
    ai_reconciliation: { name: 'AI-avstämning', icon: CheckCircle2, description: 'Matcha banktransaktioner mot bokföringen automatiskt.' },
    ai_invoice_agent: { name: 'AI-fakturaagent', icon: Bot, description: 'Låt Finn hantera leverantörsfakturor åt dig.' },
    custom_agents: { name: 'Egna agenter', icon: Bot, description: 'Bygg egna AI-agenter för företagets rutiner.' },
};

const parseFeatures = (plan) => {
    if (!plan) return {};
    if (typeof plan.features === 'string') {
        try { return JSON.parse(plan.features); } catch (_e) { return {}; }
    }
    return plan.features || {};
};

const FeatureDisabledPage = () => {
    const location = useLocation();
    const history = useHistory();
    const { companyId } = useParams();
    const { entitlements, definitions, featureName: commercialName } = useFeatures();

    const featureKey = location.state?.feature;
    const featureDef = FEATURE_DEFINITIONS[featureKey];
    const knownFeature = definitions.some(definition=>definition.key===featureKey);
    const FeatureIcon = featureDef?.icon || Lock;
    const featureName = commercialName(featureKey);

    const [plans, setPlans] = useState(null); // null = loading, [] = loaded

    const fetchPlans = useCallback(async () => {
        try {
            const { data } = await axios.get(getApiUrl('plans'), { withCredentials: true });
            setPlans(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error('Error fetching plans:', error);
            setPlans([]);
        }
    }, []);

    useEffect(() => { fetchPlans(); }, [fetchPlans]);

    const currentPlanName = entitlements?.plan?.name;
    const currentPlanDisplay = entitlements?.plan?.displayName;

    // The cheapest plan that includes the feature — the shortest path up.
    const targetPlan = (plans || [])
        .filter((plan) => parseFeatures(plan)[featureKey] === true && (plan.plan_name !== currentPlanName || plan.offer_version_id !== entitlements?.plan?.versionId))
        .sort((a, b) => (a.billing_prices?.find(price=>price.billingCycle==='monthly')?.amountMinor ?? Infinity) - (b.billing_prices?.find(price=>price.billingCycle==='monthly')?.amountMinor ?? Infinity))[0] || null;
    const targetPrice = targetPlan?.billing_prices?.find(price=>price.billingCycle==='monthly');

    return (
        <div className="fd-wrap">
            <div className="fd-card">
                <span className="fd-icon" aria-hidden="true">
                    <FeatureIcon size={26} strokeWidth={1.75} />
                </span>

                <h1 className="fd-title">{featureName} ingår inte i din plan</h1>
                {featureDef?.description && (
                    <p className="fd-description">{featureDef.description}</p>
                )}

                {/* Without a known feature (direct visit, lost router state) there is no
                    honest plan claim to make — just offer the way back. */}
                {knownFeature && plans !== null && (
                    targetPlan ? (
                        /* The upgrade path, literally: where you are → what unlocks this. */
                        <div className="fd-path" role="group" aria-label="Uppgraderingsväg">
                            {currentPlanDisplay && (
                                <>
                                    <span className="fd-plan">
                                        <span className="fd-plan__label">Din plan</span>
                                        <span className="fd-plan__name">{currentPlanDisplay}</span>
                                    </span>
                                    <ArrowRight size={16} className="fd-path__arrow" aria-hidden="true" />
                                </>
                            )}
                            <span className="fd-plan fd-plan--target">
                                <span className="fd-plan__label">Ingår i</span>
                                <span className="fd-plan__name">{targetPlan.display_name}</span>
                                <span className="fd-plan__price">
                                    {targetPrice ? `${(targetPrice.amountMinor/100).toLocaleString('sv-SE')} kr/mån` : 'Se aktuellt pris'}
                                </span>
                            </span>
                        </div>
                    ) : (
                        <p className="fd-no-plan">
                            Se tillgängliga tillägg och planer under Prenumeration.
                        </p>
                    )
                )}

                <div className="fd-actions">
                    {entitlements?.versioned && <Button variant="primary" onClick={()=>history.push(`/${companyId}/settings/subscription#tillagg`)}>Se funktionstillägg</Button>}
                    {targetPlan && (
                        <Button
                            variant="primary"
                            onClick={() => history.push(`/${companyId}/settings/subscription#planer`)}
                        >
                            Se planer och uppgradera
                            <ArrowRight size={16} className="ms-2" />
                        </Button>
                    )}
                    <Button
                        variant={targetPlan ? 'outline-secondary' : 'primary'}
                        onClick={() => history.push(`/${companyId}/dashboard`)}
                    >
                        <ArrowLeft size={16} className="me-2" />
                        Tillbaka till översikten
                    </Button>
                </div>

                {/* When the big CTA isn't shown (unknown feature, or no plan carries
                    it) the subscription page is still where plans live — offer it
                    quietly rather than not at all. */}
                {!targetPlan && (
                    <Link
                        to={`/${companyId}/settings/subscription#planer`}
                        className="fd-plans-link"
                    >
                        Se planer och prenumeration
                    </Link>
                )}
            </div>

            <style jsx>{`
                .fd-wrap {
                    display: flex;
                    justify-content: center;
                    padding: 4rem 1rem;
                }
                .fd-card {
                    width: 100%;
                    max-width: 30rem;
                    background: white;
                    border: 1px solid #e2e8f0;
                    border-radius: 0.75rem;
                    padding: 2.5rem 2rem;
                    text-align: center;
                    animation: fd-in 0.24s ease-out both;
                }
                @keyframes fd-in {
                    from { opacity: 0; transform: translateY(6px); }
                    to { opacity: 1; transform: none; }
                }
                @media (prefers-reduced-motion: reduce) {
                    .fd-card { animation: none; }
                }
                .fd-icon {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    width: 3.5rem;
                    height: 3.5rem;
                    border-radius: 50%;
                    background: var(--rgba-primary-1);
                    color: var(--primary);
                    margin-bottom: 1rem;
                }
                .fd-title {
                    font-size: 1.25rem;
                    font-weight: 700;
                    color: var(--title);
                    margin-bottom: 0.5rem;
                }
                .fd-description {
                    color: var(--secondary);
                    font-size: 0.875rem;
                    margin: 0 auto 1.5rem;
                    max-width: 24rem;
                }
                .fd-path {
                    display: inline-flex;
                    align-items: center;
                    gap: 0.75rem;
                    margin-bottom: 1.75rem;
                    flex-wrap: wrap;
                    justify-content: center;
                }
                .fd-path__arrow { color: var(--secondary); flex-shrink: 0; }
                .fd-plan {
                    display: inline-flex;
                    flex-direction: column;
                    align-items: flex-start;
                    padding: 0.5rem 1rem;
                    border: 1px solid #e2e8f0;
                    border-radius: 0.5rem;
                    background: #f8fafc;
                    text-align: left;
                }
                .fd-plan__label {
                    font-size: 0.6875rem;
                    text-transform: uppercase;
                    letter-spacing: 0.04em;
                    color: var(--secondary);
                }
                .fd-plan__name {
                    font-weight: 700;
                    font-size: 0.9375rem;
                    color: var(--title);
                }
                .fd-plan--target {
                    background: var(--rgba-primary-1);
                    border-color: var(--primary);
                }
                .fd-plan--target .fd-plan__label { color: var(--primary); }
                .fd-plan__price {
                    font-size: 0.75rem;
                    color: var(--secondary);
                }
                .fd-no-plan {
                    color: var(--secondary);
                    font-size: 0.875rem;
                    margin-bottom: 1.5rem;
                }
                .fd-actions {
                    display: flex;
                    justify-content: center;
                    gap: 0.5rem;
                    flex-wrap: wrap;
                }
                .fd-plans-link {
                    display: inline-block;
                    margin-top: 1rem;
                    font-size: 0.8125rem;
                    color: var(--primary);
                    text-decoration: none;
                }
                .fd-plans-link:hover {
                    text-decoration: underline;
                }
            `}</style>
        </div>
    );
};

export default FeatureDisabledPage;
